import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCreateTradeRequest } from 'services/repositories/relationships/relationships';
import { addNotification } from 'stores/notifications/notifications';
import { cn } from 'support/helpers/cn/cn';
import { processSubmissionErrors } from 'support/helpers/errors/errors';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { isSupplierAccordingToDocumentTypes } from 'support/helpers/relationships/relationships';
import { ConnectionPartner, ConnectionsRoutes, NetworkRoutes, PartnerDTO } from 'support/types';
import { Alert } from 'components/Display/Alert/Alert';
import { SlideOver } from 'components/Display/SlideOver/SlideOver';
import { DocumentTypeRequestModal } from 'components/DocumentTypeRequestModal/DocumentTypeRequestModal';
import { Button } from 'components/Form/Button/Button';
import { CheckboxInABox } from 'components/Form/Checkbox/Checkbox';
import { Form } from 'components/Form/Form';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';

import { DocumentSettingsCheckboxes } from '../DocumentSettingsCheckboxes/DocumentSettingsCheckboxes';
import { createDocumentTypesPayload, DocumentTypeSetting, mapPartnerDocumentTypes } from '../helpers';
import {
  PartnerIdentifiers,
  PartnerIdentifierSchema,
  PartnerIdentifierType,
} from '../PartnerIdentifiers/PartnerIdentifiers';
import { TradeRequestMessage } from '../TradeRequestMessage/TradeRequestMessage';
import { UnsupportedDocumentTypesMessage } from '../UnsupportedDocumentTypesMessage/UnsupportedDocumentTypesMessage';

type NewTradeRequestProps = {
  showPopup: boolean;
  onClose: () => void;
  partner: Pick<PartnerDTO, 'id' | 'documentTypes' | 'logoUrl' | 'name'>;
  connected: boolean;
  onBack?: () => void;
};

type NewTradeRequestForm = { message: string; includeAsCCRecipient: boolean } & PartnerIdentifierType;

export function NewTradeRequest({
  showPopup,
  onClose: setShowPopup,
  partner,
  connected,
  onBack,
}: NewTradeRequestProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);
  const [showDocumentTypeRequestModal, setShowDocumentTypeRequestModal] = useState(false);
  const { data: currentPartner } = useCurrentPartner();
  const preferredIdentifierDomain = currentPartner?.preferredIdentifierDomain;

  const formMethods = useForm<NewTradeRequestForm>({
    defaultValues: defaultData(),
    resolver: yupResolver(
      Yup.object()
        .shape({ message: Yup.string().default(''), includeAsCCRecipient: Yup.boolean().required() })
        .concat(PartnerIdentifierSchema(t, { connected, preferredIdentifierDomain, partnerInNetwork: true })),
    ),
  });

  const { register, handleSubmit, reset, setError } = formMethods;

  const partnerDocumentTypes = useMemo(
    () => mapPartnerDocumentTypes({ partnerDocuments: partner.documentTypes, isCurrentPartner: false }),
    [partner.documentTypes],
  );

  /*
  We have an error here when partner is not yet loaded, so we need to add a check for that
  */
  const [documentTypeSettings, setDocumentTypeSettings] = useState<Array<DocumentTypeSetting>>(partnerDocumentTypes);

  useEffect(() => {
    setDocumentTypeSettings(partnerDocumentTypes);
  }, [partnerDocumentTypes]);

  const { mutate, isLoading } = useCreateTradeRequest();

  const onSubmit = async (submission: NewTradeRequestForm) => {
    setNonInputErrors([]);

    if (!currentPartner) {
      return;
    }

    const relationships = createDocumentTypesPayload(
      currentPartner.id,
      partner.id,
      documentTypeSettings.filter((setting) => setting.outgoing.selected || setting.incoming.selected),
    );

    if (!relationships.length) {
      setNonInputErrors([t('partners:request.noDocumentTypesSelected')]);
      return;
    }

    mutate(
      {
        tradeRequests: [
          {
            partnerId: partner.id,
            message: submission.message,
            ...(!connected &&
              submission.partyIdentifierDomain && {
                partyIdentifierDomain: submission.partyIdentifierDomain,
                partyIdentifier: submission.partyIdentifier,
              }),
            relationships,
            includeAsCCRecipient: submission.includeAsCCRecipient,
          },
        ],
      },
      {
        onSuccess: () => {
          onClose(true);
          addNotification(t('partners:request.newTradeRequest.success'));

          const isNetworkContext = location.pathname.startsWith('/network');
          const route = isNetworkContext ? NetworkRoutes.connectionDetails : ConnectionsRoutes.connectionDetails;
          navigate(routeToPage(route, { partnerId: partner.id }));
        },
        onError: (err) => {
          processSubmissionErrors({
            error: err,
            setInputError: setError,
            setNonInputErrors,
            defaultData: defaultData(),
            t,
          });
        },
      },
    );
  };

  const onClose = (saveSelection?: boolean) => {
    const rollbackDocTypesSelection = !saveSelection;

    reset();
    setShowPopup();
    setNonInputErrors([]);
    if (rollbackDocTypesSelection) {
      setDocumentTypeSettings(partnerDocumentTypes);
    }
  };

  const onCancel = () => onClose(false);

  const hasSelectedDocumentTypes = documentTypeSettings.some(
    (setting) => setting.outgoing.selected || setting.incoming.selected,
  );

  const loggedInPartnerIsASupplier = useMemo(() => {
    const sentDocumentTypes = documentTypeSettings
      .filter((setting) => !!setting.outgoing.selected)
      .map(({ documentType }) => documentType);
    const receivedDocumentTypes = documentTypeSettings
      .filter((setting) => !!setting.incoming.selected)
      .map(({ documentType }) => documentType);

    return isSupplierAccordingToDocumentTypes(sentDocumentTypes, receivedDocumentTypes);
  }, [documentTypeSettings]);

  const isPartnerOnboarding = useMemo(() => {
    return documentTypeSettings.every(
      (setting) =>
        setting.incoming.currentStatus === 'notAvailable' && setting.outgoing.currentStatus === 'notAvailable',
    );
  }, [documentTypeSettings]);

  if (!partner) {
    return null;
  }

  return (
    <SlideOver
      size="large"
      onBack={onBack}
      footer={
        <>
          <CancelButton isLoading={isLoading} onCancel={onBack ?? onCancel} label={t('common:cancel')} />
          <PrimaryButton
            label={t('partners:request.newTradeRequest.primaryButton')}
            isLoading={isLoading}
            disabled={isPartnerOnboarding}
          />
        </>
      }
      headerImage={<PartnerLogo imageUrl={partner.logoUrl} size="large" />}
      title={partner.name}
      subtitle={
        connected ? t('partners:request.updateTradeRequest.heading') : t('partners:request.newTradeRequest.heading')
      }
      open={showPopup}
      setOpen={onClose}
    >
      <div className={cn(nonInputErrors.length ? 'mb-6' : undefined)}>
        <ValidationErrors errors={nonInputErrors} />
      </div>

      <FormProvider {...formMethods}>
        <Form
          id="newTradeRequestForm"
          onSubmit={handleSubmit(onSubmit)}
          className="divide-y divide-gray-200 *:not-first:pt-6 *:not-last:pb-6"
        >
          <div>
            {/*  div wrapper needed to handle the flex gap */}
            <DocumentSettingsCheckboxes
              banner={
                isPartnerOnboarding ? (
                  <Alert severity="warning" withoutIcon className="mb-4">
                    <span className="text-sm leading-5 font-semibold text-yellow-700">
                      {t('partners:request.partnerStillOnboarding')}
                    </span>
                  </Alert>
                ) : null
              }
              documentTypeSettings={documentTypeSettings}
              setDocumentTypeSettings={setDocumentTypeSettings}
            />
            <div className="mt-4 empty:hidden">
              <UnsupportedDocumentTypesMessage
                documentTypeSettings={documentTypeSettings}
                onRequestUnavailableDocuments={() => setShowDocumentTypeRequestModal(true)}
              />
            </div>
          </div>
          {connected ? null : (
            <PartnerIdentifiers preferredIdentifierDomain={preferredIdentifierDomain} partnerInNetwork />
          )}

          <div className="space-y-6">
            {hasSelectedDocumentTypes && !loggedInPartnerIsASupplier && <TradeRequestMessage />}

            <CheckboxInABox
              label={t('partners:request.includeAsCCRecipient.label')}
              description={t('partners:request.includeAsCCRecipient.description')}
              {...register('includeAsCCRecipient')}
            />
          </div>
        </Form>
      </FormProvider>
      <DocumentTypeRequestModal
        open={showDocumentTypeRequestModal}
        onClose={() => setShowDocumentTypeRequestModal(false)}
        requestedPartnerId={partner.id}
      />
    </SlideOver>
  );
}

export const convertToPartnerV2ToRequiredPartnerDTO = (
  partner: ConnectionPartner,
): Pick<PartnerDTO, 'id' | 'name' | 'logoUrl' | 'documentTypes'> => {
  return {
    id: partner.id,
    name: partner.name,
    logoUrl: partner.logo_url || undefined,
    documentTypes: partner.document_types,
  };
};

const defaultData = (): NewTradeRequestForm => ({
  message: '',
  includeAsCCRecipient: true,
  partyIdentifier: '',
});

const PrimaryButton = ({ isLoading, disabled, label }: { isLoading: boolean; disabled: boolean; label: string }) => (
  <Button
    loading={isLoading}
    disabled={disabled}
    type="submit"
    form="newTradeRequestForm"
    analyticsId="partners:new_trade_request_send"
  >
    <div data-testid="action-button">{label}</div>
  </Button>
);

const CancelButton = ({ isLoading, onCancel, label }: { isLoading: boolean; onCancel: () => void; label: string }) => (
  <Button disabled={isLoading} onClick={onCancel} variant="secondary" analyticsId="partners:new_trade_request_cancel">
    <div data-testid="cancel-button">{label}</div>
  </Button>
);
